import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import SubmitButton from "../submitButton/SubmitButton";
import { useNotifications } from "../../context/Notifications";
import { useTranslator } from "../../context/translator";
import Can from "../can/Can";
import { PERMISSION_CODE } from "../../utils/enum/enums";
import { ValidatePermission } from "../../utils/allowHelper/AllowHelper";
import {
  createRole,
  getRoleById,
  updateRole,
} from "../../services/roles/services";
import { useGetCompanyQuery, useGetUserByIdQuery } from "store/api/apiSlice";

const RoleEdit = ({ roleId }) => {
  const navigate = useNavigate();
  const { translate } = useTranslator();
  const { addNotification } = useNotifications();
  const { user } = useSelector((state) => state.user);
  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );
  const [role, setRole] = useState({
    name: "",
    description: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleCancel = () => {
    return navigate(-1);
  };

  const handleOnChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;

    setRole((prevValue) => {
      return {
        ...prevValue,
        [name]: value,
      };
    });
  };

  const handleSubmitRole = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsLoading(true);
    if (roleId !== "new") {
      const response = await updateRole(userProfile?.user, roleId, role);

      if (response?.role?.acknowledged) {
        addNotification({
          id: Date.now(),
          message: translate("role-updated"),
          kind: "success",
        });
        //return true; // navigate(-1);
      } else {
        addNotification({
          id: Date.now(),
          message: translate("error-updating-role"),
          kind: "danger",
        });
      }
    } else {
      const data = {
        name: role.name,
        description: role.description,
      };
      const response = await createRole(userProfile?.user, data, company?._id);

      if (response?.role?.acknowledged) {
        addNotification({
          id: Date.now(),
          message: translate("role-created"),
          kind: "success",
        });
        //return true; // navigate(-1);
      } else {
        addNotification({
          id: Date.now(),
          message: translate("error-creating-role"),
          kind: "danger",
        });
      }
    }

    setIsLoading(false);
  };

  const getRole = React.useCallback(async () => {
    const res = await getRoleById(userProfile?.user, roleId);
    const json = await res;
    setRole(json.role);
  }, [roleId, userProfile?.user]);

  useEffect(() => {
    if (roleId !== "new") {
      getRole();
    }
  }, [roleId, getRole]);

  const readOnly = React.useCallback(() => {
    const hasPermissions =
      ValidatePermission(userProfile?.user, [PERMISSION_CODE.EDIT_ROLE]) ||
      (ValidatePermission(userProfile?.user, [PERMISSION_CODE.CREATE_ROLE]) &&
        roleId === "new");

    return !hasPermissions;
  }, [roleId, userProfile?.user]);

  if (userLoading || companyLoading) return null;

  return (
    <form method="POST" onSubmit={handleSubmitRole}>
      <div>
        <h3 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
          {roleId === "new" ? translate("new-role") : role?.name}
        </h3>
      </div>
      <div className="grid gap-6 mb-6 md:grid-cols-2">
        <div>
          <label
            htmlFor="name"
            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            {translate("name")}
          </label>
          <input
            type="text"
            id="name"
            name="name"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            placeholder={translate("name")}
            value={role?.name}
            required
            onChange={handleOnChange}
            disabled={readOnly()}
          />
        </div>

        <div>
          <label
            htmlFor="description"
            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            {translate("description")}
          </label>
          <textarea
            type="text"
            id="description"
            name="description"
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            placeholder={translate("description")}
            value={role?.description}
            required
            onChange={handleOnChange}
            disabled={readOnly()}
          />
        </div>
      </div>
      <div className="flex justify-between">
        <button
          type="button"
          className="py-2.5 px-5 me-2 mb-2 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700"
          onClick={handleCancel}
        >
          {translate("cancel")}
        </button>

        <Can
          permissions={[PERMISSION_CODE.EDIT_ROLE]}
          fallback={
            <Can permissions={[PERMISSION_CODE.CREATE_ROLE]}>
              <SubmitButton
                isLoading={isLoading}
                text={translate("save")}
                textLoading={translate("saving")}
                key={"saveRole"}
              />
            </Can>
          }
        >
          <SubmitButton
            isLoading={isLoading}
            text={translate("save")}
            textLoading={translate("saving")}
            key={"saveRole"}
          />
        </Can>
      </div>
    </form>
  );
};

export default RoleEdit;
